import { AlertTriangle, CheckCircle, Copy01, Download01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { useClipboard } from "@/hooks/use-clipboard";
import { error, success } from "@/providers/ToastProvider";
import { backupCodesFile } from "@/utils/two-factor";
import { Eyebrow } from "./panel-heading";

const REVOKE_DOWNLOAD_URL_AFTER_MS = 40_000;

type Props = {
  eyebrow?: string;
  title: string;
  description: string;
  replaceWarning?: string;
  codes: string[];
  onDone: () => void;
};

export const BackupCodesPanel = ({ eyebrow, title, description, replaceWarning, codes, onDone }: Props) => {
  const { copy } = useClipboard();

  const copyAll = async () => {
    const { success: copied } = await copy(codes.join("\n"));
    if (copied) success("Backup codes copied");
    else error("Couldn't copy");
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([backupCodesFile(codes)], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "unbox-backup-codes.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DOWNLOAD_URL_AFTER_MS);
  };

  return (
    <div className="flex max-w-[560px] flex-col gap-5 p-6 pb-9">
      <div className="flex flex-col gap-1">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <div className="flex items-center gap-2">
          <CheckCircle className="size-5 text-success-600" aria-hidden />
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      {replaceWarning && (
        <p className="flex w-fit items-center gap-2.5 rounded-lg bg-warning-50 px-3.5 py-2.5 text-sm text-warning-700">
          <AlertTriangle className="size-[18px] shrink-0" aria-hidden />
          {replaceWarning}
        </p>
      )}
      <ul className="grid grid-cols-2 gap-2" aria-label="Backup codes">
        {codes.map((code) => (
          <li
            key={code}
            className="rounded-lg bg-white px-3 py-2 text-center font-mono text-sm tracking-wide text-gray-800"
          >
            {code}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3">
        <Button color="secondary" iconLeading={Copy01} onClick={copyAll}>
          Copy all
        </Button>
        <Button color="secondary" iconLeading={Download01} onClick={download}>
          Download .txt
        </Button>
        <Button className="ml-auto" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};
